import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import requestEditionsService from "../services/requestEditionsService";
import onboardingService from "../services/onboardingService";
import { RequestEditionPlan } from "../types/requestEdition";

type Step = "select" | "review" | "progress";

interface Props {
  requestId: string;
  requestContext: string;
  requestType: string;
  onClose: () => void;
}

const RequestEditionGenerator: React.FC<Props> = ({
  requestId,
  requestContext,
  requestType,
  onClose,
}) => {
  const navigate = useNavigate();

  // Step 1: Select
  const [blueprints, setBlueprints] = useState<any[]>([]);
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<number | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState("");

  // Step 2: Review
  const [plan, setPlan] = useState<RequestEditionPlan | null>(null);

  // Step 3: Progress
  const [currentStep, setCurrentStep] = useState<Step>("select");
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [editionId, setEditionId] = useState<number | null>(null);
  const [generateError, setGenerateError] = useState("");
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadBlueprints();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const loadBlueprints = async () => {
    try {
      const data = await onboardingService.listOnboardingBlueprints();
      setBlueprints(data);
      if (data.length > 0) setSelectedBlueprintId(data[0].id);
    } catch (err) {
      console.error("Failed to load blueprints:", err);
    }
  };

  const handleGeneratePlan = async () => {
    if (!selectedBlueprintId) return;
    setPlanLoading(true);
    setPlanError("");
    try {
      const result = await requestEditionsService.generatePlan(requestId, selectedBlueprintId);
      setPlan(result);
      setCurrentStep("review");
    } catch (err: any) {
      setPlanError(err.response?.data?.error || "Failed to generate plan. Please try again.");
    } finally {
      setPlanLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!plan || !selectedBlueprintId) return;
    setCurrentStep("progress");
    setGenerateError("");
    setProgress(0);
    setProgressMessage("Starting email generation...");

    try {
      const result = await requestEditionsService.generate(requestId, selectedBlueprintId, plan);
      startPolling(result.job_id);
    } catch (err: any) {
      setGenerateError(err.response?.data?.error || "Failed to start generation.");
    }
  };

  const startPolling = (jobId: string) => {
    pollingRef.current = setInterval(async () => {
      try {
        const status = await requestEditionsService.getStatus(jobId);
        setProgress(status.progress);
        if (status.progress_message) setProgressMessage(status.progress_message);

        if (status.status === "completed") {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          setEditionId(status.edition_id || null);
          setProgressMessage("Email created successfully!");
        } else if (status.status === "failed") {
          clearInterval(pollingRef.current!);
          pollingRef.current = null;
          setGenerateError(status.error_message || "Email generation failed.");
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 3000);
  };

  const renderSelect = () => (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h3 style={{ margin: 0 }}>Generate Email from Request</h3>
        <button
          onClick={onClose}
          style={{
            padding: "4px 12px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            backgroundColor: "#fff",
            cursor: "pointer",
            fontSize: "13px",
          }}
        >
          Cancel
        </button>
      </div>
      <p style={{ color: "#6b7280", marginBottom: "16px", fontSize: "14px" }}>
        Select a blueprint and we'll generate an email plan from this request's content.
      </p>

      <div style={{ marginBottom: "16px" }}>
        <label style={{ display: "block", fontWeight: 600, marginBottom: "6px", fontSize: "14px" }}>
          Blueprint
        </label>
        <select
          value={selectedBlueprintId || ""}
          onChange={(e) => setSelectedBlueprintId(Number(e.target.value))}
          style={{
            width: "100%",
            padding: "8px 12px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "14px",
          }}
        >
          {blueprints.map((bp) => (
            <option key={bp.id} value={bp.id}>
              {bp.name || bp.newsletter_name || `Blueprint ${bp.id}`}
              {bp.reference_edition_title ? ` — ${bp.reference_edition_title}` : ""}
            </option>
          ))}
        </select>
      </div>

      {planError && (
        <div style={{ color: "#dc2626", backgroundColor: "#fef2f2", padding: "12px", borderRadius: "4px", marginBottom: "16px" }}>
          {planError}
        </div>
      )}

      <button
        onClick={handleGeneratePlan}
        disabled={planLoading || !selectedBlueprintId}
        style={{
          padding: "10px 24px",
          backgroundColor: "#000",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          cursor: planLoading || !selectedBlueprintId ? "not-allowed" : "pointer",
          opacity: planLoading || !selectedBlueprintId ? 0.5 : 1,
          fontSize: "14px",
        }}
      >
        {planLoading ? "Generating plan..." : "Generate Plan"}
      </button>
    </div>
  );

  const renderReview = () => {
    if (!plan) return null;
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ margin: 0 }}>Review Email Plan</h3>
          <button
            onClick={() => setCurrentStep("select")}
            style={{
              padding: "4px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              backgroundColor: "#fff",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            Back
          </button>
        </div>
        <p style={{ color: "#6b7280", marginBottom: "16px", fontSize: "14px" }}>
          Review and edit the email plan below, then generate the email.
        </p>

        <div style={{ marginBottom: "12px" }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: "4px", fontSize: "14px" }}>Title</label>
          <input
            type="text"
            value={plan.title}
            onChange={(e) => setPlan({ ...plan, title: e.target.value })}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ marginBottom: "12px" }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: "4px", fontSize: "14px" }}>Description</label>
          <textarea
            value={plan.description}
            onChange={(e) => setPlan({ ...plan, description: e.target.value })}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              fontSize: "14px",
              minHeight: "80px",
              resize: "vertical",
              boxSizing: "border-box",
            }}
          />
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: "4px", fontSize: "14px" }}>Content Guidance</label>
          <textarea
            value={plan.content_guidance}
            onChange={(e) => setPlan({ ...plan, content_guidance: e.target.value })}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              fontSize: "14px",
              minHeight: "80px",
              resize: "vertical",
              boxSizing: "border-box",
            }}
          />
        </div>

        <button
          onClick={handleGenerate}
          style={{
            padding: "10px 24px",
            backgroundColor: "#000",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          Generate Email
        </button>
      </div>
    );
  };

  const renderProgress = () => {
    const isComplete = editionId !== null;
    const isFailed = !!generateError;

    return (
      <div>
        <h3 style={{ marginBottom: "16px" }}>
          {isComplete ? "Email Created!" : isFailed ? "Generation Failed" : "Generating Email..."}
        </h3>

        {!isFailed && (
          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                width: "100%",
                height: "8px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                overflow: "hidden",
                marginBottom: "8px",
              }}
            >
              <div
                style={{
                  width: `${isComplete ? 100 : progress}%`,
                  height: "100%",
                  backgroundColor: isComplete ? "#22c55e" : "#000",
                  borderRadius: "4px",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
            <div style={{ color: "#6b7280", fontSize: "13px" }}>{progressMessage}</div>
          </div>
        )}

        {generateError && (
          <div style={{ color: "#dc2626", backgroundColor: "#fef2f2", padding: "12px", borderRadius: "4px", marginBottom: "16px" }}>
            {generateError}
          </div>
        )}

        {isComplete && (
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => navigate(`/newsletterEdition/${editionId}`)}
              style={{
                padding: "10px 24px",
                backgroundColor: "#000",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              Open in Editor
            </button>
            <button
              onClick={onClose}
              style={{
                padding: "10px 24px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                backgroundColor: "#fff",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              Close
            </button>
          </div>
        )}

        {isFailed && (
          <button
            onClick={() => {
              setGenerateError("");
              setCurrentStep("review");
            }}
            style={{
              padding: "8px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "4px",
              backgroundColor: "#fff",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            Back to Review
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        padding: "20px",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        backgroundColor: "#fff",
        marginBottom: "24px",
      }}
    >
      {currentStep === "select" && renderSelect()}
      {currentStep === "review" && renderReview()}
      {currentStep === "progress" && renderProgress()}
    </div>
  );
};

export default RequestEditionGenerator;
